import { useParams, Navigate, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Calendar, Clock, HardHat, ArrowLeft, Check, ArrowUpRight, Play } from "lucide-react";
import { PROJECTS } from "../../data/site";
import { Nav } from "./Nav";
import { Footer } from "./Footer";
import { Reveal, Kicker } from "./Reveal";
import { scrollToSection } from "../../lib/scroll";

const EASE = [0.16, 1, 0.3, 1];

export const CaseStudy = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const project = PROJECTS.find((p) => p.slug === slug);
  if (!project) return <Navigate to="/" replace />;
  const others = PROJECTS.filter((p) => p.slug !== slug).slice(0, 3);

  const goHome = (hash) => {
    navigate("/");
    setTimeout(() => scrollToSection(hash), 400);
  };

  const meta = [
    { icon: MapPin, label: "Location", value: project.location },
    { icon: HardHat, label: "Service", value: project.service },
    { icon: Calendar, label: "Completed", value: project.year },
    { icon: Clock, label: "Duration", value: project.duration },
  ];

  return (
    <div data-testid="case-study-page">
      <Nav />

      <section className="relative flex min-h-[62vh] items-end overflow-hidden">
        <img
          src={project.img}
          alt={project.title}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-white/60" />
        <div className="absolute inset-0 bg-gradient-to-t from-white via-white/30 to-white/20" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 pb-16 pt-44">
          <button
            data-testid="case-back-btn"
            onClick={() => goHome("#projects")}
            className="mb-8 flex items-center gap-3 text-[11px] uppercase tracking-[0.3em] text-black/60 transition-colors duration-300 hover:text-bronze"
          >
            <ArrowLeft className="h-4 w-4" /> All Projects
          </button>
          <div className="flex items-center gap-4">
            <span className="h-px w-10 bg-bronze" />
            <span className="text-xs font-medium uppercase tracking-[0.35em] text-bronze">
              {project.service} — Case Study
            </span>
          </div>
          <motion.h1
            data-testid="case-title"
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE }}
            className="mt-6 font-display text-5xl font-extrabold uppercase leading-[0.95] tracking-tight text-ink md:text-7xl"
          >
            {project.title}
          </motion.h1>
        </div>
      </section>

      <section className="border-y border-black/10 bg-coal">
        <div className="mx-auto grid max-w-7xl grid-cols-2 px-6 md:grid-cols-4">
          {meta.map((m, i) => {
            const Icon = m.icon;
            return (
              <div
                key={m.label}
                data-testid={`case-meta-${m.label.toLowerCase()}`}
                className={`flex items-center gap-4 py-7 ${i > 0 ? "border-l border-black/10 pl-6" : ""}`}
              >
                <Icon className="h-5 w-5 shrink-0 text-bronze" strokeWidth={1.8} />
                <div>
                  <div className="text-[11px] uppercase tracking-[0.25em] text-black/50">
                    {m.label}
                  </div>
                  <div className="mt-1 text-sm font-medium text-ink">{m.value}</div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto grid max-w-7xl gap-14 px-6 lg:grid-cols-2">
          <Reveal>
            <Kicker index="01" label="Overview" />
            <h2 className="mt-6 font-display text-4xl font-bold uppercase tracking-tight text-ink md:text-5xl">
              The Brief &amp; The Build
            </h2>
            <p className="mt-6 leading-relaxed text-fog">{project.overview}</p>
          </Reveal>
          <Reveal delay={0.12}>
            <Kicker index="02" label="Scope of Works" />
            <ul className="mt-8 space-y-4">
              {project.scope.map((item) => (
                <li
                  key={item}
                  className="flex items-start gap-4 border-b border-black/5 pb-4"
                >
                  <span className="mt-0.5 grid h-6 w-6 shrink-0 place-items-center border border-bronze/40">
                    <Check className="h-3.5 w-3.5 text-bronze" strokeWidth={2.2} />
                  </span>
                  <span className="text-sm leading-relaxed text-ink/80">{item}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      <section className="border-y border-black/5 bg-coal py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <Kicker index="03" label="Transformation" />
            <h2 className="mt-6 font-display text-4xl font-bold uppercase tracking-tight text-ink md:text-5xl">
              {project.before ? (
                <>
                  Before <span className="text-outline">&amp;</span>{" "}
                  <span className="text-gold-shine">After</span>
                </>
              ) : (
                <>
                  The <span className="text-gold-shine">Finished Build</span>
                </>
              )}
            </h2>
          </Reveal>
          <div className={`mt-12 grid gap-6 ${project.before ? "md:grid-cols-2" : ""}`}>
            {(project.before
              ? [
                  { label: "Before — during the build", src: project.before },
                  { label: "After — completed", src: project.img },
                ]
              : [{ label: "Completed project", src: project.img }]
            ).map((shot) => (
              <Reveal key={shot.label}>
                <figure className="relative overflow-hidden border border-black/10">
                  <img
                    src={shot.src}
                    alt={`${project.title} — ${shot.label}`}
                    className="h-[320px] w-full object-cover md:h-[420px]"
                    loading="lazy"
                  />
                  <figcaption className="absolute left-5 top-5 bg-white/85 px-3 py-2 text-[11px] uppercase tracking-[0.2em] text-ink/85 backdrop-blur-md">
                    {shot.label}
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <Kicker index="04" label="Project Film" />
          </Reveal>
          {project.video ? (
            <video
              data-testid="case-video"
              src={project.video}
              controls
              playsInline
              className="mt-10 w-full border border-black/10"
            />
          ) : (
            <div
              data-testid="case-video-placeholder"
              className="mt-10 flex aspect-video flex-col items-center justify-center gap-5 border border-black/10 bg-coal px-6"
            >
              <span className="grid h-16 w-16 place-items-center rounded-full bg-gold-shine">
                <Play className="ml-1 h-6 w-6 fill-ink text-ink" />
              </span>
              <p className="font-display text-2xl font-bold uppercase tracking-wide text-ink">
                Film coming soon
              </p>
              <p className="max-w-md text-center text-sm leading-relaxed text-fog">
                We're preparing footage from this build — meanwhile, watch
                recent site films in the Our Work in Action gallery on the
                homepage.
              </p>
            </div>
          )}
        </div>
      </section>

      <section className="bg-gold-shine py-16 md:py-20">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-8 px-6 md:flex-row md:items-center">
          <h2 className="font-display text-4xl font-extrabold uppercase leading-[0.95] tracking-tight text-ink md:text-5xl">
            Planning something
            <br />
            similar?
          </h2>
          <button
            data-testid="case-cta-quote"
            onClick={() => goHome("#contact")}
            className="flex items-center gap-3 bg-ink px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-bronze transition-colors duration-300 hover:bg-black"
          >
            Get a Free Quote <ArrowUpRight className="h-4 w-4" />
          </button>
        </div>
      </section>

      <section className="bg-white py-20 md:py-28">
        <div className="mx-auto max-w-7xl px-6">
          <Reveal>
            <Kicker index="05" label="Keep Exploring" />
            <h2 className="mt-6 font-display text-4xl font-bold uppercase tracking-tight text-ink md:text-5xl">
              More Projects
            </h2>
          </Reveal>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {others.map((p) => (
              <button
                key={p.slug}
                data-testid={`case-more-${p.slug}`}
                onClick={() => navigate(`/projects/${p.slug}`)}
                className="group relative block h-64 cursor-pointer overflow-hidden border border-black/10 text-left"
              >
                <img
                  src={p.img}
                  alt={p.title}
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-white/95 via-white/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-bronze">
                    {p.service}
                  </span>
                  <span className="mt-1 block font-display text-xl font-bold uppercase tracking-wide text-ink">
                    {p.title}
                  </span>
                </div>
              </button>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};
